import { create } from 'zustand'

interface TokenState {
  idToken: string | null
  /** IdToken の有効期限（Unix タイムスタンプ・秒） */
  expiresAt: number | null
  /** 認証チェック完了前は true。setToken / clearToken 呼び出し後に false になる */
  loading: boolean
  /**
   * 認証付き API が 401 を返したとき true になる。
   * _app.tsx の useEffect がこのフラグを監視し、XState に UNAUTHORIZED_401 イベントを送信する。
   * 送信完了後は clearPendingTokenRefresh() で false に戻す。
   */
  pendingTokenRefresh: boolean
  /**
   * 401 起因のトークンリフレッシュが完了した後にページをリロードすべきとき true になる。
   * リフレッシュ完了を検知した _app.tsx の useEffect がリロード後に false に戻す。
   */
  shouldReloadAfterRefresh: boolean
  /**
   * 認証済み API が 403 LINE_NOT_FRIEND を返したとき true になる。
   * _app.tsx の useEffect がこのフラグを監視し、
   * LINE友達追加強制リダイレクトを行う。発火後は clearPendingLineFriendRequired() で false に戻す。
   */
  pendingLineFriendRequired: boolean
  /**
   * 認証済み API が 403 REAUTH_REQUIRED を返したとき true になる（#3610 / #3635）。
   * _app.tsx の useEffect がこのフラグを監視し、auth の再認証画面へ移動する。
   * 発火後は clearPendingReauthRequired() で false に戻す。
   */
  pendingReauthRequired: boolean
  /**
   * true のとき、token/refresh 完了後のページリロードを抑止する。
   * ARカメラ表示中など、ページリロードがユーザー体験を損なう場面で使用する。
   * arNavigate state 侵入時に true、離脱時に false となる。
   */
  pageReloadSuppressed: boolean
}

interface TokenStore extends TokenState {
  setToken: (idToken: string, expiresAt: number) => void
  clearToken: () => void
  /** 認証付き API が 401 を返したときに呼び出す。React フック外（request.ts 等）からも呼び出し可能 */
  notify401: () => void
  /** XState へのイベント送信完了後に呼び出してフラグをリセットする */
  clearPendingTokenRefresh: () => void
  /** ページリロード完了後に呼び出してフラグをリセットする */
  clearShouldReloadAfterRefresh: () => void
  /** 認証済み API が 403 LINE_NOT_FRIEND を返したときに呼び出す。React フック外（request.ts 等）からも呼び出し可能 */
  notifyLineFriendRequired: () => void
  /** リダイレクト処理完了後に呼び出してフラグをリセットする */
  clearPendingLineFriendRequired: () => void
  /** 認証済み API が 403 REAUTH_REQUIRED を返したときに呼び出す。React フック外（request.ts 等）からも呼び出し可能 */
  notifyReauthRequired: () => void
  /** リダイレクト処理完了後に呼び出してフラグをリセットする */
  clearPendingReauthRequired: () => void
  /** ページリロード抑止を有効化する（ARカメラ起動時に呼び出す） */
  suppressPageReload: () => void
  /** ページリロード抑止を解除する（ARカメラ終了時に呼び出す） */
  restorePageReload: () => void
}

export const useTokenStore = create<TokenStore>((set) => ({
  idToken: null,
  expiresAt: null,
  loading: true,
  pendingTokenRefresh: false,
  shouldReloadAfterRefresh: false,
  pendingLineFriendRequired: false,
  pendingReauthRequired: false,
  pageReloadSuppressed: false,
  setToken: (idToken, expiresAt) => set({ idToken, expiresAt, loading: false }),
  clearToken: () => set({ idToken: null, expiresAt: null, loading: false }),
  notify401: () => set({ pendingTokenRefresh: true, shouldReloadAfterRefresh: true }),
  clearPendingTokenRefresh: () => set({ pendingTokenRefresh: false }),
  clearShouldReloadAfterRefresh: () => set({ shouldReloadAfterRefresh: false }),
  notifyLineFriendRequired: () => set({ pendingLineFriendRequired: true }),
  clearPendingLineFriendRequired: () => set({ pendingLineFriendRequired: false }),
  notifyReauthRequired: () => set({ pendingReauthRequired: true }),
  clearPendingReauthRequired: () => set({ pendingReauthRequired: false }),
  suppressPageReload: () => set({ pageReloadSuppressed: true }),
  restorePageReload: () => set({ pageReloadSuppressed: false }),
}))
